import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, Dimensions } from 'react-native';
import { WindowInformation } from '../services/window-information';
import ContentContainer from './ContentContainer';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import TopMenu from './TopMenu';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { NavigationContainerRefWithCurrent, NavigationProp, useNavigation } from '@react-navigation/native';
import { RootStackParamList } from '../typings/Params';
import { useAuth } from '../context/Auth';
import { applicationPages, Module, ModulePage, modulePages, RootScreen } from '../Routes';
import GenericLateralMenu from './GenericLateralMenu';

type DefaultPageLayoutProps = {
    children: React.ReactNode | undefined;
    navigationRef: NavigationContainerRefWithCurrent<RootStackParamList>;
};

const screenWidth = Dimensions.get('window').width;

export default function DefaultPageLayout({ children, navigationRef }: DefaultPageLayoutProps) {
    const { styles } = useStyles(stylesheet);
    const insets = useSafeAreaInsets();
    const windowInfo = WindowInformation();
    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);

    const navigation = useNavigation<NavigationProp<RootStackParamList>>();

    const { user: authenticatedUser } = useAuth();

    navigationRef.addListener('state', () => {
        forceUpdate();
    });

    const pageName = navigationRef.getCurrentRoute()?.name;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let page: { modulePath?: string; screenObj: ModulePage<any, any> | RootScreen; } | undefined;

    if (pageName) {
        page = modulePages[pageName];
    }

    const [showLateralMenu, setShowLateralMenu] = useState<boolean>(localStorage.getItem('HAMBURGER_OPEN') == 'true');

    const animationOpacity = useRef(new Animated.Value(0)).current;
    const animationTranslateX = useRef(new Animated.Value(-screenWidth)).current;

    useEffect(() => {
        Animated.timing(animationOpacity, {
            toValue: showLateralMenu ? 0.6 : 0,
            duration: 200,
            useNativeDriver: true
        }).start();

        Animated.timing(animationTranslateX, {
            toValue: showLateralMenu ? 0 : -screenWidth,
            duration: 0,
            useNativeDriver: true
        }).start();
    }, [showLateralMenu]);

    useEffect(() => {
        if (windowInfo.isMobile) {
            setShowLateralMenu(!windowInfo.isMobile);
        } else {
            getStorage();
        }
    }, [windowInfo.isMobile]);

    useEffect(() => {
        if (!windowInfo.isMobile) {
            setStorage(showLateralMenu);
        }
    }, [showLateralMenu, windowInfo.isMobile]);

    async function getStorage() {
        try {
            const hamburgerOpen = await AsyncStorage.getItem('HAMBURGER_OPEN');
            if (hamburgerOpen) {
                setShowLateralMenu(JSON.parse(hamburgerOpen));
            }
        } catch (e) {
            console.error('Error on parse Storage: ', e);
        }
    }

    async function setStorage(showLateralMenu: boolean) {
        await AsyncStorage.setItem('HAMBURGER_OPEN', String(showLateralMenu));
    }

    let thisModule;

    if (page && page.modulePath) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        thisModule = Object.values(applicationPages.modules).find(m => m.path === page?.modulePath) as (Module<any> | undefined);
    }

    return (
        <View style={styles.container}>
            {page && !page.screenObj.hideTopMenu &&
                <View style={styles.topBarContainer}>
                    <TopMenu navigation={navigation} setOpen={setShowLateralMenu} showDropDownOnMobile={page.screenObj.label === 'menu'} />
                </View>}

            <View style={{ flexDirection: 'row' }}>

                <View style={{ zIndex: 2, height: windowInfo.height - styles.topBarContainer.height - insets.top - insets.bottom }}>
                    {
                        thisModule && page &&
                        <GenericLateralMenu
                            navigation={navigation}
                            authenticatedUser={authenticatedUser}
                            open={showLateralMenu}
                            setOpen={setShowLateralMenu}
                            selectedMenu={page.screenObj.selectedItem || ''}
                            moduleObj={thisModule}
                        />
                    }
                </View>

                <View style={styles.contentContainer}>
                    <ContentContainer
                        headerHeight={styles.topBarContainer.height}
                        noPadding={page?.screenObj.ignorePadding}
                    >
                        {children}
                    </ContentContainer>
                </View>

                {
                    windowInfo.isMobile &&
                    <Animated.View onTouchEnd={() => setShowLateralMenu(false)} style={{
                        position: 'absolute',
                        top: 0,
                        transform: [{
                            translateX: animationTranslateX
                        }],
                        height: '100%',
                        backgroundColor: '#313131',
                        opacity: animationOpacity,
                        width: '100%',
                    }}
                    />
                }
            </View>


        </View >
    );
}


const stylesheet = createStyleSheet(() => ({
    container: {
        flex: 1,
        flexDirection: 'column',
    },

    contentContainer: {
        flex: 1,
    },

    topBarContainer: {
        height: 50,
    }
}));
